/**
 * Tesseract module configuration (#67a).
 *
 * Single switchboard for which product modules a deployment ships. Drives:
 *   1. the home context tiles (ContextSelector),
 *   2. route mounting (App.tsx),
 *   3. (indirectly) the sidebar, which is already management-context driven.
 *
 * Modules are the à-la-carte sellable units. A fork enables only what the
 * customer bought via the build-time env var `VITE_MODULES` (comma-separated
 * module keys, e.g. `VITE_MODULES=timesheet,clm`). When unset/empty, ALL
 * modules are enabled — so the demo and every existing build are unchanged.
 *
 * Core modules (`core: true`) are infrastructure and can't be turned off.
 *
 * Delivery model is clone-per-customer (own deployment + Supabase per client,
 * ITAR physical isolation) — see Tasks/Delivery_Model_Decision_2026-06-20.md.
 * So module selection is a per-deployment build choice, not runtime
 * multi-tenancy. #67d (module-aware build) will additionally drop disabled
 * modules from the bundle; this layer is the runtime gate that #67d builds on.
 */
import type { ManagementContext } from '@/store/appStore'

export type ModuleKey =
  | 'ppm'
  | 'clm'
  | 'timesheet'
  | 'supplychain'
  | 'personnel'
  | 'organizational'
  | 'mywork'
  | 'configuration'
  | 'workflow-management'
  | 'cost-management'
  | 'crm'
  | 'analytics'
  | 'it-ticketing'

export interface ModuleDef {
  key: ModuleKey
  label: string
  description: string
  /** Management contexts this module owns (gates tiles + routes). */
  contexts: ManagementContext[]
  /** Core modules are always enabled and never appear as a toggle. */
  core: boolean
}

export const MODULE_REGISTRY: ModuleDef[] = [
  {
    key: 'ppm',
    label: 'Portfolio & Project Management',
    description: 'Portfolios, projects, scheduling, earned value, budgeting, and reporting.',
    contexts: ['portfolio', 'project'],
    core: false,
  },
  {
    key: 'clm',
    label: 'Contract Lifecycle Management',
    description: 'Master contracts, task orders, CLINs, clause templates, and approvals.',
    contexts: ['contract'],
    core: false,
  },
  {
    key: 'timesheet',
    label: 'Timesheet & Manufacturing',
    description: 'Time entry, wand kiosk, floor activity, approvals, and compliance.',
    contexts: ['timesheet'],
    core: false,
  },
  {
    key: 'supplychain',
    label: 'Supply Chain',
    description: 'Contract-aware planning: demand, supply/MRP, inventory, capacity, GPD pegging, promising, control tower, compliance, sustainment, and S&OP.',
    contexts: ['supplychain'],
    core: false,
  },
  {
    key: 'personnel',
    label: 'Personnel',
    description: 'HCM user directory and personnel attributes for project assignments.',
    contexts: ['personnel'],
    core: false,
  },
  {
    key: 'organizational',
    label: 'Organizational Planning',
    description: 'Org hierarchy, cost centers, resource pools, and headcount planning.',
    contexts: ['organizational'],
    core: false,
  },
  {
    key: 'mywork',
    label: 'My Work',
    description: 'Personal command center — briefing, inbox, tasks, and approvals.',
    contexts: ['mywork'],
    core: true,
  },
  {
    key: 'workflow-management',
    label: 'Workflow and Forms Management',
    description: 'Design, run, and govern workflows and forms across every context.',
    contexts: ['workflow-management'],
    core: false,
  },
  {
    key: 'cost-management',
    label: 'Cost Management',
    description: 'Allocations, burden rates, cost transfers, cost pools and rate segments, and CAS incurred cost reporting on the S/4HANA universal ledger.',
    contexts: ['cost'],
    core: false,
  },
  {
    key: 'crm',
    label: 'Customer Relationship Management',
    description: 'Accounts, contacts, interactions, pipeline, and quoting on the S/4HANA customer master and sellable catalog.',
    contexts: ['crm'],
    core: false,
  },
  {
    key: 'it-ticketing',
    label: 'IT Ticketing',
    description: 'IT help-desk ticketing: requests, workflow approvals, delegations, and reporting.',
    contexts: ['it-ticketing'],
    core: false,
  },
  {
    key: 'configuration',
    label: 'Configuration',
    description: 'Users, roles, policies, catalogs, integrations, and system setup.',
    contexts: ['configuration'],
    core: true,
  },
  {
    key: 'analytics',
    label: 'Analytics',
    description: 'Conversational analytics across SAP S/4HANA and Tesseract data: ask in plain language, get tables, AI-chosen charts, and saveable reports.',
    contexts: ['analytics'],
    core: false,
  },
]

/** Resolve the enabled module set from `VITE_MODULES`. Unset/empty = all on. */
function resolveEnabledModules(): Set<ModuleKey> {
  // Cast: VITE_MODULES is optional and not declared on the strict ImportMetaEnv.
  const raw = (import.meta.env as Record<string, string | undefined>).VITE_MODULES?.trim()
  if (!raw) return new Set(MODULE_REGISTRY.map((m) => m.key))

  const requested = new Set(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  )
  const enabled = new Set<ModuleKey>()
  for (const m of MODULE_REGISTRY) {
    if (m.core || requested.has(m.key)) enabled.add(m.key)
  }
  return enabled
}

const ENABLED_MODULES = resolveEnabledModules()

const CONTEXT_TO_MODULE = new Map<ManagementContext, ModuleKey>()
for (const m of MODULE_REGISTRY) {
  for (const c of m.contexts) CONTEXT_TO_MODULE.set(c, m.key)
}

/** Is a given module enabled for this deployment? */
export function isModuleEnabled(key: ModuleKey): boolean {
  return ENABLED_MODULES.has(key)
}

/** Modules enabled for this deployment, in registry order. */
export function enabledModules(): ModuleDef[] {
  return MODULE_REGISTRY.filter((m) => ENABLED_MODULES.has(m.key))
}

/**
 * Is a management context's owning module enabled? Used to gate home tiles
 * and routes. A context with no owning module (shouldn't happen) stays
 * visible so nothing silently disappears.
 */
export function isContextEnabled(ctx: ManagementContext): boolean {
  const key = CONTEXT_TO_MODULE.get(ctx)
  return key ? isModuleEnabled(key) : true
}
